'use client';

import { Button } from '@/components/ui/button';
import Link from 'next/link';
import Image from 'next/image';
import ChatInput from './chat-input';
import TrustedCompanies from './trusted-companies';
import { motion } from 'framer-motion';
import DashboardPreview from './dashboard-preview';

export default function Hero() {
  return (
    <section className="relative pt-24 md:pt-40 pb-10 md:pb-16 bg-white dark:bg-black overflow-hidden">
      {/* Yellow gradient background */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[500px] opacity-40 blur-[100px] pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at center, rgba(77, 46, 245, 0.3), rgba(125, 81, 237, 0.1), transparent)',
        }}
        aria-hidden="true"
      />

      {/* Subtle diagonal lines */}
      <div
        className="absolute inset-0 overflow-hidden opacity-10 pointer-events-none"
        aria-hidden="true"
      >
        <div
          className="absolute top-0 left-0 size-[200%] -translate-x-1/2 -translate-y-1/2"
          style={{
            backgroundImage:
              'linear-gradient(45deg, rgba(77, 46, 245, 0.1) 25%, transparent 25%, transparent 50%, rgba(77, 46, 245, 0.1) 50%, rgba(77, 46, 245, 0.1) 75%, transparent 75%, transparent)',
            backgroundSize: '100px 100px',
          }}
        />
      </div>

      <div className="container mx-auto px-4 z-10">
        <div className="flex flex-col items-center">
          {/* Logo and name */}
          <motion.div
            className="hidden md:flex items-center mb-4 md:mb-16"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="relative size-16 md:size-20 mr-3 md:mr-4 overflow-hidden rounded-2xl bg-[#4d2ef5]/10 dark:bg-[#4d2ef5]/0 flex items-center justify-center">
              <Image
                src="/android-chrome-512x512.png"
                alt="Stock Penguins Logo"
                width={60}
                height={60}
                className="object-contain scale-75 md:scale-100"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-transparent to-white/10 dark:to-black/10 pointer-events-none" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-zinc-900 dark:text-white tracking-tight">
              StockPenguins
            </h2>
          </motion.div>

          <div className="mx-auto max-w-4xl text-center">
            <motion.h1
              className="mb-4 md:mb-6 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter text-zinc-900 dark:text-white leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Unleash the power of{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#4d2ef5] to-[#7d51ed]">
                AI
              </span>{' '}
              in Stock Investing
            </motion.h1>

            <motion.p
              className="mb-8 md:mb-12 text-lg md:text-xl text-zinc-600 dark:text-zinc-300 tracking-tight max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Your AI companion for insightful investment research and real-time
              financial data. One simple interface, for all your investing
              needs.
            </motion.p>

            {/* Chat input with example questions */}
            <motion.div
              className="mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <ChatInput />
            </motion.div>

            <motion.div
              className="flex flex-col sm:flex-row gap-3 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <Link href="/chat" prefetch={false} passHref>
                <Button className="w-full sm:w-auto text-sm h-12 px-8 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-white dark:bg-[#4d2ef5] dark:hover:bg-[#3f25cc] dark:text-white font-medium shadow-lg shadow-zinc-900/10 dark:shadow-[#4d2ef5]/20">
                  Get started
                </Button>
              </Link>
              <Link
                href="https://whop.com/stockpenguins-com-investing/"
                target="_blank"
                rel={'noopener noreferrer'}
                passHref
              >
                <Button
                  variant="outline"
                  className="w-full sm:w-auto text-sm h-12 px-8 rounded-xl border-zinc-200 text-zinc-600 hover:text-zinc-900 dark:border-zinc-700 dark:text-zinc-300 dark:hover:text-white"
                >
                  Join our community
                </Button>
              </Link>
            </motion.div>
          </div>

          {/* App screenshot/mockup */}
          <DashboardPreview />

          {/* Trusted by section */}
          <TrustedCompanies />
        </div>
      </div>
    </section>
  );
}
